import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { dashboardAPI, accountsAPI, transferAPI } from '../services/api';
import { handleApiError } from '../utils/errorHandler';
import { formatCurrency } from '../utils/currencyFormatter';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const notification = useNotification();
  
  // Currency formatter for user's currency
  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };
  
  const [data, setData] = useState({
    accounts: [],
    transactions: [],
    totalBalance: 0,
    summary: null,
    monthlySpending: []
  });
  const [loading, setLoading] = useState(false);
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [error, setError] = useState(null);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await dashboardAPI.getDashboard();
      
      if (response?.status === 'success') {
        const dashboardData = response.data;
        
        // Process accounts with growth calculation
        const processedAccounts = dashboardData.accounts.map(account => ({
          ...account,
          growth: calculateGrowth(account)
        }));
        
        // Process transactions - now they already include bill payments as proper transaction records
        const processedTransactions = dashboardData.recent_transactions.map(transaction => {
          const category = categorizeTransaction(transaction.description);
          const icon = getTransactionIcon(transaction.description, category);
          
          return {
            id: transaction.id,
            type: transaction.type,
            description: transaction.description,
            amount: transaction.amount,
            transaction_type: transaction.type,
            date: transaction.transaction_date || transaction.created_at || transaction.date,
            status: transaction.status,
            category: category,
            account: transaction.account,
            reference_number: transaction.reference_number,
            metadata: transaction.metadata,
            icon: icon
          };
        });
        
        setData({
          accounts: processedAccounts,
          totalBalance: dashboardData.total_balance || 0,
          transactions: processedTransactions,
          summary: {
            cashback: dashboardData.cashback || null,
            growth_percentage: '+7.2%', // Mock data
            savings_goal_percentage: '72%', // Mock data
            savings_goal_label: 'Savings Goal'
          }
        });
      } else {
        throw new Error(response?.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
      notification.showError(
        'Failed to load dashboard data. Please try again.',
        'Error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const calculateGrowth = (account) => {
    // This would ideally be calculated based on historical data
    // For now, return a simple calculation or default
    const growthValue = Math.random() * 10; // Random for demo, replace with real calculation
    return `+${growthValue.toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toISOString().split('T')[0];
    try {
      return new Date(dateString).toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  const categorizeTransaction = (description) => {
    if (!description) return 'Other';
    
    const desc = description.toLowerCase();
    if (desc.includes('zelle')) return 'Zelle';
    if (desc.includes('salary') || desc.includes('deposit') || desc.includes('income')) return 'Income';
    if (desc.includes('amazon') || desc.includes('store') || desc.includes('shop')) return 'Shopping';
    if (desc.includes('restaurant') || desc.includes('food') || desc.includes('dining')) return 'Dining';
    if (desc.includes('electric') || desc.includes('gas') || desc.includes('water') || desc.includes('utility')) return 'Utilities';
    if (desc.includes('transfer')) return 'Transfer';
    if (desc.includes('payment')) return 'Payment';
    return 'Other';
  };

  const getTransactionIcon = (description, category) => {
    if (!description && !category) return '💳';
    
    const text = (description + ' ' + category).toLowerCase();
    if (text.includes('zelle')) return '⚡';
    if (text.includes('salary') || text.includes('income')) return '💰';
    if (text.includes('amazon') || text.includes('shop')) return '🛒';
    if (text.includes('restaurant') || text.includes('dining') || text.includes('food')) return '🍽️';
    if (text.includes('electric') || text.includes('utility')) return '⚡';
    if (text.includes('gas') || text.includes('fuel')) return '⛽';
    if (text.includes('transfer')) return '💸';
    if (text.includes('payment')) return '💳';
    if (text.includes('coffee')) return '☕';
    if (text.includes('movie') || text.includes('entertainment')) return '🎬';
    if (text.includes('uber') || text.includes('taxi') || text.includes('transport')) return '🚗';
    return '💳';
  };

  const generateMonthlySpending = (transactions) => {
    // Generate mock monthly data - in a real app, this would come from the API
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      amount: Math.floor(Math.random() * 2000) + 1500 // Random spending between $1500-$3500
    }));
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const handleQuickAction = (actionName) => {
    switch (actionName) {
      case 'Transfer':
        navigate('/transfer');
        break;
      case 'Pay Bills':
        navigate('/bill-pay');
        break;
      case 'Zelle':
        navigate('/zelle');
        break;
      case 'Invest':
        navigate('/crypto-trading');
        break;
      case 'Cards':
        navigate('/cards');
        break;
      case 'Loans':
        navigate('/loans');
        break;
      case 'Cashback':
        // Navigate to cashback page or show modal with details
        notification.showInfo(
          `Available cashback: ${formatCurrencyWithUserCurrency(data.summary?.cashback?.available_balance || 0)}`,
          'Cashback Details'
        );
        break;
      case 'Support':
        navigate('/support');
        break;
      default:
        console.log(`Action ${actionName} not implemented yet`);
    }
  };

  const quickActions = [
    { id: 1, name: 'Transfer', icon: '💸', gradient: 'from-blue-500 to-blue-600' },
    { id: 2, name: 'Pay Bills', icon: '📋', gradient: 'from-green-500 to-green-600' },
    { id: 3, name: 'Zelle', icon: '📱', gradient: 'from-purple-500 to-purple-600' },
    { id: 4, name: 'Cashback', icon: '💰', gradient: 'from-emerald-500 to-emerald-600' },
    { id: 5, name: 'Invest', icon: '📊', gradient: 'from-indigo-500 to-indigo-600' },
    { id: 6, name: 'Cards', icon: '💳', gradient: 'from-pink-500 to-pink-600' },
    { id: 7, name: 'Support', icon: '💬', gradient: 'from-teal-500 to-teal-600' }
  ];

  // Helper function to format activity date for display
  const formatActivityDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Helper function to get status badge color for transactions
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed':
      case 'posted':
        return 'bg-green-100 text-green-700';
      case 'processing':
        return 'bg-yellow-100 text-yellow-700';
      case 'pending':
        return 'bg-blue-100 text-blue-700';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const openAllTransactions = async () => {
    try {
      setLoadingAll(true);
      setShowAllTransactions(true);
      const res = await dashboardAPI.getComprehensiveHistory();
      if (res?.status === 'success') {
        const list = Array.isArray(res.data) ? res.data : (res.data?.transactions || []);
        const processed = list.map((t) => ({
          id: t.id,
          type: t.type,
          description: t.description,
          amount: t.amount,
          date: t.transaction_date || t.created_at || t.date,
          status: t.status,
          category: categorizeTransaction(t.description),
          account: t.account,
          reference_number: t.reference_number,
          icon: getTransactionIcon(t.description, categorizeTransaction(t.description))
        }));
        setAllTransactions(processed);
      } else {
        setAllTransactions([]);
      }
    } catch (e) {
      setAllTransactions([]);
    } finally {
      setLoadingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* App Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/logos/app-icon.png" 
                alt="First Heritage Community Bank" 
                className="w-8 h-8 rounded-lg"
              />
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {getGreeting()}, {user?.name?.split(' ')[0] || 'User'}
                </h1>
                <p className="text-xs text-gray-500">Welcome back</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <button className="hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-blue-100">
                  <img 
                    src={user?.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=3b82f6&color=fff&size=32`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pb-6">
        {/* Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 mb-4"
        >
          <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10 rounded-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-blue-200 text-sm">Total Balance</p>
                  <div className="flex items-center space-x-2">
                    <p className="text-2xl md:text-3xl font-bold">
                      {balanceVisible ? formatCurrencyWithUserCurrency(data.totalBalance) : '••••••'}
                    </p>
                    <button
                      onClick={() => setBalanceVisible(!balanceVisible)}
                      className="p-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {balanceVisible ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <button
                    onClick={fetchDashboardData}
                    disabled={loading}
                    className="mb-1 p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-50"
                    title="Refresh data"
                  >
                    <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <div className="flex items-center text-green-300 mb-1">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span className="text-xs font-medium">
                      {data.summary?.growth_percentage || '+7.2%'}
                    </span>
                  </div>
                  <p className="text-blue-200 text-xs">This month</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-white/20">
                <div>
                  <p className="text-blue-200 text-xs">Accounts</p>
                  <p className="text-lg font-bold">{data.accounts.length}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-xs">
                    {data.summary?.savings_goal_label || 'Savings Goal'}
                  </p>
                  <p className="text-lg font-bold text-green-300">
                    {data.summary?.savings_goal_percentage || '72%'}
                  </p>
                </div>
                <div>
                  <p className="text-blue-200 text-xs">Cashback</p>
                  <p className="text-lg font-bold text-yellow-300">
                    {data.summary?.cashback?.available_balance ? formatCurrencyWithUserCurrency(data.summary.cashback.available_balance) : formatCurrencyWithUserCurrency(0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions - Bybit Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-5"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900">Quick Actions</h3>
            <button className="text-blue-600 text-sm font-medium">More</button>
          </div>
          
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex space-x-3 pb-2" style={{ width: 'max-content' }}>
              {quickActions.map((action, index) => (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex-shrink-0 bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all min-w-[80px]"
                  onClick={() => handleQuickAction(action.name)}
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${action.gradient} rounded-xl flex items-center justify-center mb-2 mx-auto`}>
                    <span className="text-white text-lg">{action.icon}</span>
                  </div>
                  <p className="font-medium text-gray-900 text-xs text-center leading-tight">{action.name}</p>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Account Cards - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-5"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900">My Accounts</h3>
            <button 
              className="text-blue-600 text-sm font-medium"
              onClick={() => navigate('/accounts')}
            >
              View All
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Regular Bank Account Cards */}
            {data.accounts.length > 0 ? (
              data.accounts.slice(0, data.summary?.cashback ? 3 : 4).map((account, index) => (
                <motion.div
                  key={account.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate('/accounts')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      account.type === 'checking' ? 'bg-blue-100' :
                      account.type === 'savings' ? 'bg-green-100' :
                      account.type === 'investment' ? 'bg-purple-100' :
                      account.type === 'business' ? 'bg-orange-100' :
                      'bg-gray-100'
                    }`}>
                      {account.type === 'checking' && (
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      )}
                      {account.type === 'savings' && (
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-4 0H3m2-16h14" />
                        </svg>
                      )}
                      {account.type === 'investment' && (
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      )}
                      {account.type === 'business' && (
                        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8h4M10 12h4" />
                        </svg>
                      )}
                      {!['checking', 'savings', 'investment', 'business'].includes(account.type) && (
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      account.growth.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {account.growth}
                    </span>
                  </div>

                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{account.name}</h4>
                  <p className="text-lg font-bold text-gray-900 mb-1">
                    {balanceVisible ? formatCurrencyWithUserCurrency(account.balance) : '••••••'}
                  </p>
                  <p className="text-gray-500 text-xs">{account.accountNumber}</p>
                </motion.div>
              ))
            ) : !data.summary?.cashback ? (
              <div className="col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-4 0H3m2-16h14" />
                </svg>
                <p className="text-gray-500 text-sm">No accounts available</p>
                <button 
                  onClick={() => navigate('/accounts')}
                  className="mt-2 text-blue-600 text-sm font-medium"
                >
                  Set up accounts
                </button>
              </div>
            ) : null}

            {/* Cashback Balance Card - Last */}
            {data.summary?.cashback && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + (data.accounts.length * 0.1) }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleQuickAction('Cashback')}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                    +{formatCurrencyWithUserCurrency(data.summary.cashback.this_month_earned)}
                  </span>
                </div>

                <h4 className="font-semibold text-gray-900 text-sm mb-1">Cashback Rewards</h4>
                <p className="text-lg font-bold text-gray-900 mb-1">
                  {balanceVisible ? formatCurrencyWithUserCurrency(data.summary.cashback.available_balance) : '••••••'}
                </p>
                <p className="text-gray-500 text-xs">Available Balance</p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Transaction History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overflow-hidden"
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Transaction History</h2>
              <button className="text-green-600 hover:text-green-700 text-sm font-medium" onClick={openAllTransactions}>
                View All
              </button>
            </div>
            {data.transactions?.length > 0 ? (
              <div className="space-y-3">
                {data.transactions.slice(0, 8).map((transaction, index) => (
                  <div key={transaction.id || index} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                        {transaction.icon || (transaction.type === 'credit' ? '💰' : '💳')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {transaction.description}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 truncate">
                          <span className="truncate">{transaction.category}</span>
                          {transaction.account && (
                            <>
                              <span className="flex-shrink-0">•</span>
                              <span className="truncate">{transaction.account}</span>
                            </>
                          )}
                          {transaction.reference_number && (
                            <>
                              <span className="flex-shrink-0">•</span>
                              <span className="truncate">Ref: {transaction.reference_number}</span>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {formatDate(transaction.date)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className={`font-semibold text-sm ${
                        (transaction.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(transaction.amount || 0) >= 0 ? '+' : ''}
                        {formatCurrencyWithUserCurrency(Math.abs(transaction.amount || 0))}
                      </p>
                      <div className="flex items-center justify-end space-x-1 text-xs">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          transaction.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <p className="text-gray-500">No recent transactions</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* All Transactions Modal */}
        {showAllTransactions && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-11/12 max-w-3xl max-h-[80vh] overflow-hidden">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">All Transactions</h3>
                <button onClick={() => setShowAllTransactions(false)} className="text-gray-500 hover:text-gray-700 text-sm font-medium">Close</button>
              </div>
              <div className="p-6 overflow-y-auto" style={{maxHeight: 'calc(80vh - 64px)'}}>
                {loadingAll ? (
                  <div className="py-12 text-center text-gray-500">Loading...</div>
                ) : allTransactions.length > 0 ? (
                  <div className="space-y-3">
                    {allTransactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                            {transaction.icon || (transaction.type === 'credit' ? '💰' : '💳')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{transaction.description}</p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500 truncate">
                              <span className="truncate">{transaction.category}</span>
                              {transaction.account && (<><span className="flex-shrink-0">•</span><span className="truncate">{transaction.account}</span></>)}
                              {transaction.reference_number && (<><span className="flex-shrink-0">•</span><span className="truncate">Ref: {transaction.reference_number}</span></>)}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{formatDate(transaction.date)}</div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className={`font-semibold text-sm ${(transaction.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(transaction.amount || 0) >= 0 ? '+' : ''}{formatCurrencyWithUserCurrency(Math.abs(transaction.amount || 0))}
                          </p>
                          <div className="flex items-center justify-end space-x-1 text-xs">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              transaction.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {transaction.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-gray-500">No transactions found</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 